import React from "react";
import _ from "lodash";

// Import Highcharts
import Highcharts from "highcharts";
import drilldown from "highcharts/modules/drilldown.js";
import HighchartsReact from "highcharts-react-official";

import { formatMoneyWithCurrency } from "../../home-dashboard-v2/helpers/reports-helper";
import InfoDescriptionTooltip from "../../home-dashboard-v2/components/info-desc-tooltip";

drilldown(Highcharts);

Highcharts.setOptions({
  lang: {
    decimalPoint: ".",
    thousandsSep: ",",
  },
  credits: {
    enabled: false,
  },
});

class RevenueDistributionDrilldownPieChart extends React.Component {
  constructor(props) {
    super(props);

    const {
      reports,
      metricKey,
      selectedCurrency,
      selectedPubId,
      selectedNetworkId,
      selectedYieldSetIds,
      selectedRequestType,
      isRequestTypeDimension
    } = props;

    this.state = {
      metricKey: props.metricKey,
      options: _createChartOptions({
        reports,
        metricKey,
        selectedCurrency,
        selectedPubId,
        selectedNetworkId,
        selectedYieldSetIds,
        selectedRequestType,
        isRequestTypeDimension
      }),
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.startDate !== this.props.startDate ||
      prevProps.endDate !== this.props.endDate ||
      prevProps.metricKey !== this.props.metricKey ||
      prevProps.selectedPubId !== this.props.selectedPubId ||
      prevProps.selectedNetworkId !== this.props.selectedNetworkId ||
      prevProps.selectedYieldSetIds !== this.props.selectedYieldSetIds ||
      prevProps.selectedCurrency !== this.props.selectedCurrency ||
      prevProps.selectedRequestType !== this.props.selectedRequestType ||
      prevProps.isRequestTypeDimension !== this.props.isRequestTypeDimension
    ) {
      const {
        reports,
        metricKey,
        selectedCurrency,
        selectedPubId,
        selectedNetworkId,
        selectedYieldSetIds,
        selectedRequestType,
        isRequestTypeDimension
      } = this.props;

      this.setState({
        options: _createChartOptions({
          reports,
          metricKey,
          selectedCurrency,
          selectedPubId,
          selectedNetworkId,
          selectedYieldSetIds,
          selectedRequestType,
          isRequestTypeDimension
        }),
      });
    }
  }

  render() {
    const { options } = this.state;
    const { selectedPubId, selectedNetworkId, selectedYieldSetIds, isRequestTypeDimension } =
      this.props;

    let chartTitleEle = "";
    if (selectedPubId === -1) {
      chartTitleEle = <>Publishers</>;
    } else if (isRequestTypeDimension) {
      chartTitleEle = <>Request Type</>;
    } else if (selectedNetworkId !== -1 || selectedYieldSetIds !== -1) {
      chartTitleEle = (
        <>
          Yield Sets
          <InfoDescriptionTooltip
            metricKey="YIELD_SET"
            place="top"
          ></InfoDescriptionTooltip>
        </>
      );
    } else {
      chartTitleEle = <>Networks</>;
    }

    return (
      <div className="text-center mt-2">
        <div style={styles.statTitle}>{chartTitleEle}</div>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          allowChartUpdate={true}
          immutable={true} // this fixes chart not updating after selecting ys then selecting all ys
        />
      </div>
    );
  }
}

function _createChartOptionsByPublisher({
  reports,
  metricKey,
  selectedCurrency,
  selectedPubId,
}) {
  // Breakdown by:
  // 1. Publishers
  // 2. Networks
  // 3. YieldSets

  let pubTotal = _.sumBy(reports.publishers, metricKey);

  let selectedNetworkPerc, selectedNetworkName;

  const pubData = _.map(reports.publishers, (n) => {
    const d = n[metricKey];
    const p = _.round((d / pubTotal) * 100, 2);

    const valueFormatted = formatMoneyWithCurrency({
      currency: selectedCurrency,
      value: d,
    });

    if (n.id === selectedPubId) {
      selectedNetworkPerc = p;
      selectedNetworkName = n.name;
    }

    const r = {
      name: n.name,
      y: d,
      valueFormatted,
      perc: p,
    };

    return r;
  });

  const metricKeyName = _.startCase(metricKey);

  const options = {
    title: {
      text: "",
    },
    chart: {
      type: "pie",
      height: 242,
      backgroundColor: "rgba(0,0,0,0)",
    },

    plotOptions: {
      pie: {
        size: "100%",
        // showInLegend: true,
        dataLabels: {
          enabled: true,
          distance: 4,
          format: "<b>{point.name}</b> ({point.percentage:.2f}%)",
        },
        states: {
          hover: {
            halo: {
              opacity: 0.1,
            },
          },
        },
      },
    },

    tooltip: {
      useHTML: true,
      formatter: function () {
        return `
          <table>
            <thead>
              <tr>
                <th style="font-size: 14px; font-weight: bold;" colspan="2">
                  ${this.point.name}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  ${metricKeyName}
                </td>
                <td style="font-weight: bold;">
                  ${this.point.valueFormatted}
                </td>
              </tr>
              <tr>
                <td>
                  % of total
                </td>
                <td style="font-weight: bold;">
                  ${this.point.perc}%
                </td>
              </tr>
            </tbody>
          </table>
        `;
      },
    },

    series: [
      {
        name: "Publishers",
        // colorByPoint: true,
        data: pubData,
      },
    ],
  };

  return options;
}

function _createChartOptions({
  reports,
  metricKey,
  selectedCurrency,
  selectedPubId,
  selectedNetworkId,
  selectedYieldSetIds,
  selectedRequestType,
  isRequestTypeDimension
}) {
  if (selectedPubId === -1) {
    return _createChartOptionsByPublisher({
      reports,
      metricKey,
      selectedCurrency,
      selectedPubId,
      selectedNetworkId,
      selectedYieldSetIds,
    });
  }

  let isDrilldown = true;
  if (selectedNetworkId !== -1 || selectedYieldSetIds !== -1 || isRequestTypeDimension) {
    isDrilldown = false;
  }

  const metricKeyName = _.startCase(metricKey);

  let seriesName = "";
  let seriesData = [];

  if (isDrilldown) {
    if (selectedPubId !== -1 && selectedNetworkId === -1) {
      reports.networks = _.filter(reports.networks, { pubId: selectedPubId });
    }

    const networkTotal = _.sumBy(reports.networks, metricKey);
    const networkData = _.map(reports.networks, (n) => {
      const d = n[metricKey];
      const p = _.round((d / networkTotal) * 100, 2);

      const valueFormatted = formatMoneyWithCurrency({
        currency: selectedCurrency,
        value: d,
      });

      const r = {
        name: n.name,
        y: d,
        valueFormatted,
        perc: p,
      };

      return r;
    });

    seriesName = "Networks";
    seriesData = networkData;
  } else if (isRequestTypeDimension) {
    const rtTotal = _.sumBy(reports.requestTypes, metricKey);

    const rtDataGroupByRequestType = _.groupBy(reports.requestTypes, "name");
    const rtSeriesData = _.map(
      rtDataGroupByRequestType,
      (requestTypes, requestType) => {
        const metricKeyTotal = _.sumBy(requestTypes, metricKey);
        const isSelected = _.indexOf(selectedRequestType, requestType) !== -1;

        const data = {
          name: requestType,
          y: metricKeyTotal,
          valueFormatted: formatMoneyWithCurrency({
            currency: selectedCurrency,
            value: metricKeyTotal,
          }),
          perc: _.round((metricKeyTotal / rtTotal) * 100, 2),
        };

        if (_.isArray(selectedRequestType)) {
          data.color = isSelected ? "" : "lightgray";
          data.dataLabels = {
            enabled: isSelected,
            color: isSelected ? "#000" : "lightgray",
          };

          data.sliced = isSelected;
          data.selected = isSelected;
        }

        return data;
      }
    );

    seriesName = "Request Types";
    seriesData = rtSeriesData;
  } else {
    const ysTotal = _.sumBy(reports.yieldSets, metricKey);
    const ysDataGroupByNetwork = _.groupBy(reports.yieldSets, "gamNetworkId");
    const ysSeriesData = [];
    _.forEach(ysDataGroupByNetwork, (yieldSets, networkId) => {
      const ysData = {
        name: networkId,
        id: networkId,
        data: _.map(yieldSets, (ys) => {
          const d = ys[metricKey];
          const p = _.round((d / ysTotal) * 100, 2);

          const valueFormatted = formatMoneyWithCurrency({
            currency: selectedCurrency,
            value: d,
          });

          let isSelectedYs = _.indexOf(selectedYieldSetIds, ys.id) !== -1;

          const r = {
            name: ys.name,
            y: d,
            valueFormatted,
            perc: p,
          };

          if (selectedYieldSetIds !== -1) {
            r.color = isSelectedYs ? "" : "lightgray";
            r.dataLabels = {
              enabled: isSelectedYs,
              color: isSelectedYs ? "#000" : "lightgray",
            };

            r.sliced = isSelectedYs;
            r.selected = isSelectedYs;
          }

          return r;
        }),
      };

      ysSeriesData.push(ysData);
    });

    seriesName = "Yield Sets";
    seriesData = _.get(
      _.find(ysSeriesData, { id: selectedNetworkId.toString() }),
      "data",
      []
    );
  }

  const options = {
    title: {
      text: "",
    },
    chart: {
      type: "pie",
      height: 242,
      backgroundColor: "rgba(0,0,0,0)",
    },

    plotOptions: {
      pie: {
        size: "100%",
        // showInLegend: true,
        dataLabels: {
          enabled: true,
          distance: 4,
          format: "<b>{point.name}</b> ({point.percentage:.2f}%)",
        },
        states: {
          hover: {
            halo: {
              opacity: 0.1,
            },
          },
        },
      },
    },

    // legend: {
    //   layout: "vertical",
    //   enabled: true,
    //   floating: true,
    //   align: "right",
    //   verticalAlign: "middle",
    //   labelFormat: "{name} {percentage:.2f}%",
    // },

    tooltip: {
      useHTML: true,
      formatter: function () {
        return `
          <table>
            <thead>
              <tr>
                <th style="font-size: 14px; font-weight: bold;" colspan="2">
                  ${this.point.name}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  ${metricKeyName}
                </td>
                <td style="font-weight: bold;">
                  ${this.point.valueFormatted}
                </td>
              </tr>
              <tr>
                <td>
                  % of total
                </td>
                <td style="font-weight: bold;">
                  ${this.point.perc}%
                </td>
              </tr>
            </tbody>
          </table>
        `;
      },
    },

    series: [
      {
        name: seriesName,
        // colorByPoint: true,
        data: seriesData,
      },
    ],
  };

  return options;
}

const styles = {
  statTitle: {
    color: "#556377",
    fontWeight: "500",
  },
};

export default RevenueDistributionDrilldownPieChart;
